// about-us.tsx

import { Box, Grid, Typography } from '@mui/material';
import ManageWebApi from 'api/application/manageWeb';
import { notiError, notiSuccess } from 'component/notifications/notifications';
import MainButton from 'new-components/buttons/main-button';
import InputTextEditor from 'new-components/inputs/text-editor';
import AlertModal from 'new-components/modal/alert-modal';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const AboutUs = () => {
    const { t } = useTranslation();
    const T = 'MANAGE_WEB.ABOUT_US';
    const [loading, setLoading] = useState(true);
    const [contentWeb, setContentWeb] = useState<any>({});
    const [isEdit, setIsEdit] = useState(false);
    const [contentLength, setContentLength] = useState(0);
    const [errorMaxLength, setErrorMaxLength] = useState(false);
    const [isOpenAlertModal, setIsOpenAlertModal] = useState(false);

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await ManageWebApi.getAboutUs();
            setContentWeb(res.data || {});
        } catch (e) {
            console.log(e)
        }
        setLoading(false);
    };

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (!loading && !contentWeb.content) {
            setIsEdit(true);
        }
    }, [loading, contentWeb.content]);

    const onEdit = (flag: boolean) => {
        setIsEdit(flag);
        const plain = (contentWeb.content || '').replace(/<[^>]+>/g, '');
        setContentLength(plain.length);
        setErrorMaxLength(plain.length > 1000);
    };

    const onDel = async () => {
        const resp = await ManageWebApi.delAboutUs(contentWeb.aboutUsMinimalId);
        if (resp === 'pass') {
            notiSuccess(t(`${T}.DEL_SUC`));
            await loadData();
        } else {
            notiError(t(`${T}.SAVE_ERROR`));
        }
        setIsOpenAlertModal(false);
    };

    const onSave = async () => {
        const payload = { content: contentWeb.content };
        let res;
        if (contentWeb.aboutUsMinimalId) {
            res = await ManageWebApi.updateAboutUs(
                contentWeb.aboutUsMinimalId,
                { ...payload, aboutUsMinimalId: contentWeb.aboutUsMinimalId }
            );
        } else {
            res = await ManageWebApi.saveAboutUs(payload);
        }
        if (res[1]) {
            notiSuccess(t(`${T}.SAVE_SUC`));
            setIsEdit(false);
            await loadData();
        } else {
            notiError(t(`${T}.SAVE_ERROR`));
        }
    };


    const handleEditor = (value: any, id?: number) => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = value || '';
        const plainText = tempDiv.textContent || tempDiv.innerText || '';
        const trimmedText = plainText.trim();
        setContentLength(trimmedText.length);

        if (trimmedText.length > 1000) {
            setErrorMaxLength(true)
        } else {
            setContentWeb({ ...contentWeb, content: value })
            setErrorMaxLength(false)
        }
    }

    return (
        <>
            <AlertModal
                isOpen={isOpenAlertModal}
                title={t(`MANAGE_WEB.TITLE_DEL`)}
                message={t(`${T}.NOTI_DEL`)}
                type="error"
                handleClose={() => setIsOpenAlertModal(false)}
                onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
                onSubmit={{ onClick: onDel, title: t('BUTTON.SUBMIT') }}
            />

            {!loading && (
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4} xl={6}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontSize: { xs: 18, sm: 20 },
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    textAlign: { xs: 'left', sm: 'left' },
                                }}
                            >
                                {t(`${T}.TITLE`)}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={8} xl={6} display='flex' justifyContent={'flex-end'}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: 1,
                                    flexWrap: 'wrap',
                                }}
                            >
                                {isEdit ? (
                                    <>
                                        <MainButton
                                            title={t(`${T}.BTN_CANCEL`)}
                                            variant="outlined"
                                            type="secondary"
                                            onClick={() => onEdit(false)}
                                            style={{ width: 120 }}
                                            disabled={contentLength === 0}
                                        />
                                        <MainButton
                                            title={t(`${T}.BTN_SAVE`)}
                                            variant="contained"
                                            type="primary"
                                            onClick={onSave}
                                            disabled={errorMaxLength || contentLength === 0}
                                            style={{ width: 120 }}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <MainButton
                                            title={t(`${T}.BTN_EDIT`)}
                                            variant="outlined"
                                            type="primary"
                                            onClick={() => onEdit(true)}
                                            style={{ width: 120 }}
                                        />
                                        <MainButton
                                            title={t(`${T}.BTN_DEL`)}
                                            variant="contained"
                                            type="danger"
                                            onClick={() => setIsOpenAlertModal(true)}
                                            style={{ width: 120 }}
                                        />
                                    </>
                                )}
                            </Box>
                        </Grid>
                    </Grid>

                    {!isEdit &&
                        <Box sx={{ marginTop: '32px', height: '100%', overflow: 'auto' }}>
                            <Box dangerouslySetInnerHTML={{ __html: contentWeb?.content }} />
                        </Box>
                        ||
                        <Box
                            sx={{
                                width: '100%',
                                mt: 2,
                                minHeight: 200,
                                maxHeight: '60vh',
                                overflowY: 'auto'
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                {errorMaxLength && (
                                    <Typography variant="body2" color="error">
                                        {t(`${T}.MAX_LENGTH`)}
                                    </Typography>
                                )}
                                <Typography variant="body2" sx={{ ml: 1 }}>
                                    {`${contentLength}/1000`}
                                </Typography>
                            </Box>
                            <InputTextEditor
                                value={contentWeb.content || ''}
                                setValue={handleEditor}
                            />
                        </Box>
                    }

                </Box>
            )}
        </>
    );
};

export default AboutUs;
